import Badge from "../../components/badge";
import { getDisplayStatus } from "./underwritingUtils";
import {
  PolicyAttachmentTimelineEvent,
  UnderwritingPolicyAttachmentStatus,
} from "../../models/underwritingPolicyAttachment";
import "./timelineStyles.css";
import { renderApplicabilityMetadata } from "./underwritingUtils";
import { deriveAttachmentActorInfoMap } from "./actorUtils";

interface ActiveSnapshotBannerProps {
  events: PolicyAttachmentTimelineEvent[];
}

interface AttachmentSnapshot {
  attachmentId: string;
  policies: string[];
  status: UnderwritingPolicyAttachmentStatus;
  experimentName?: string;
  applicabilityMetadata?: PolicyAttachmentTimelineEvent["applicabilityMetadata"];
}

function deriveCurrentSnapshot(
  events: PolicyAttachmentTimelineEvent[]
): AttachmentSnapshot[] {
  const latestByAttachment = new Map<string, PolicyAttachmentTimelineEvent>();

  for (const event of events) {
    const currentLatest = latestByAttachment.get(event.attachmentId);
    if (
      !currentLatest ||
      new Date(event.timestamp).getTime() >
        new Date(currentLatest.timestamp).getTime()
    ) {
      latestByAttachment.set(event.attachmentId, event);
    }
  }

  return Array.from(latestByAttachment.values())
    .filter(
      (event) =>
        event.status === UnderwritingPolicyAttachmentStatus.ACTIVE ||
        event.status === UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT
    )
    .map((event) => ({
      attachmentId: event.attachmentId,
      policies: event.policies,
      status: event.status,
      experimentName: event.experimentName,
      applicabilityMetadata: event.applicabilityMetadata,
    }));
}

const STATUS_BADGE_MAP: Record<
  UnderwritingPolicyAttachmentStatus,
  "success" | "pending" | "reject"
> = {
  [UnderwritingPolicyAttachmentStatus.ACTIVE]: "success",
  [UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT]: "success",
  [UnderwritingPolicyAttachmentStatus.SHADOW]: "pending",
  [UnderwritingPolicyAttachmentStatus.INACTIVE]: "reject",
};

export default function ActiveSnapshotBanner({
  events,
}: ActiveSnapshotBannerProps) {
  const snapshot = deriveCurrentSnapshot(events);
  const actorInfoMap = deriveAttachmentActorInfoMap(events);

  function formatDate(dateStr?: string): string {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const mins = String(d.getMinutes()).padStart(2, "0");
    const secs = String(d.getSeconds()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${mins}:${secs}`;
  }

  if (snapshot.length === 0) {
    return (
      <div className="rf-snapshot-banner rf-snapshot-banner--empty">
        <span className="rf-snapshot-banner-title">Current State</span>
        <p className="rf-snapshot-banner-empty-text">
          No active or experimenting policies for this employer.
        </p>
      </div>
    );
  }

  const activeItems = snapshot.filter(
    (s) => s.status === UnderwritingPolicyAttachmentStatus.ACTIVE
  );
  const experimentItems = snapshot.filter(
    (s) => s.status === UnderwritingPolicyAttachmentStatus.ACTIVE_EXPERIMENT
  );

  return (
    <div className="rf-snapshot-banner">
      <span className="rf-snapshot-banner-title">Current State</span>
      <div className="rf-snapshot-cards">
        {activeItems.map((item) => (
          <div
            key={item.attachmentId}
            className="rf-snapshot-card rf-snapshot-card--active"
          >
            <div className="rf-snapshot-card-header">
              <span className="rf-snapshot-card-id">{item.attachmentId}</span>
              <Badge type={STATUS_BADGE_MAP[item.status]} capitalize>
                {getDisplayStatus(item.status).toLowerCase()}
              </Badge>
            </div>
            <div className="rf-snapshot-card-body">
              <div>
                <span className="rf-snapshot-card-label">Policies:</span>{" "}
                {item.policies.join(", ")}
              </div>
              {item.applicabilityMetadata && (
                <div>
                  <span className="rf-snapshot-card-label">Applicability:</span>{" "}
                  {renderApplicabilityMetadata(item.applicabilityMetadata)}
                </div>
              )}
              <div>
                <span className="rf-snapshot-card-label">Requested By:</span>{" "}
                {actorInfoMap.get(item.attachmentId)?.createdBy
                  ? `${actorInfoMap.get(item.attachmentId)?.createdBy} (${formatDate(
                      actorInfoMap.get(item.attachmentId)?.createdAt
                    )})`
                  : "-"}
              </div>
              <div>
                <span className="rf-snapshot-card-label">Approved By:</span>{" "}
                {actorInfoMap.get(item.attachmentId)?.approvedBy
                  ? `${actorInfoMap.get(item.attachmentId)?.approvedBy} (${formatDate(
                      actorInfoMap.get(item.attachmentId)?.approvedAt
                    )})`
                  : "-"}
              </div>
            </div>
          </div>
        ))}
        {experimentItems.map((item) => (
          <div
            key={item.attachmentId}
            className="rf-snapshot-card rf-snapshot-card--experiment"
          >
            <div className="rf-snapshot-card-header">
              <span className="rf-snapshot-card-id">{item.attachmentId}</span>
              <Badge type={STATUS_BADGE_MAP[item.status]} capitalize>
                {getDisplayStatus(item.status).toLowerCase()}
              </Badge>
            </div>
            <div className="rf-snapshot-card-body">
              <div>
                <span className="rf-snapshot-card-label">Policies:</span>{" "}
                {item.policies.join(", ")}
              </div>
              {item.experimentName && (
                <div>
                  <span className="rf-snapshot-card-label">Experiment:</span>{" "}
                  {item.experimentName}
                </div>
              )}
              {item.applicabilityMetadata && (
                <div>
                  <span className="rf-snapshot-card-label">Applicability:</span>{" "}
                  {renderApplicabilityMetadata(item.applicabilityMetadata)}
                </div>
              )}
              <div>
                <span className="rf-snapshot-card-label">Requested By:</span>{" "}
                {actorInfoMap.get(item.attachmentId)?.createdBy
                  ? `${actorInfoMap.get(item.attachmentId)?.createdBy} (${formatDate(
                      actorInfoMap.get(item.attachmentId)?.createdAt
                    )})`
                  : "-"}
              </div>
              <div>
                <span className="rf-snapshot-card-label">Approved By:</span>{" "}
                {actorInfoMap.get(item.attachmentId)?.approvedBy
                  ? `${actorInfoMap.get(item.attachmentId)?.approvedBy} (${formatDate(
                      actorInfoMap.get(item.attachmentId)?.approvedAt
                    )})`
                  : "-"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
